import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  ActionIcon,
  Center,
  Divider,
  Drawer,
  Group,
  Loader,
  NumberInput,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconArrowDownRight,
  IconArrowUpRight,
  IconChevronLeft,
  IconChevronRight,
  IconFileText,
  IconInfoCircle,
  IconPencil,
  IconTableExport,
} from '@tabler/icons-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { LoadFailed } from '../../components/ui/LoadFailed';
import { NodeListPreview, pruneNodes } from '../../components/essays/NodeListPreview';
import {
  ResultRow,
  downloadResultsXlsx,
  useScoreResults,
  useSetFinalScore,
  MAX_SCORE,
  MIN_SCORE,
} from '../../hooks/useQuarterlyScoreApi';
import { useQuarterlyReport } from '../../hooks/useQuarterlyApi';
import {
  isBeforeQuarter,
  reportingQuarter,
  shiftQuarter,
} from '../../components/essays/quarterPeriod';
import { ApiError } from '../../utils/api';
import { showToast } from '../../utils/toast';

const round1 = (value: number) => Math.round(value * 10) / 10;

/** Рост или падение против прошлого квартала. Без прошлого сравнивать не с чем. */
const Delta: React.FC<{ row: ResultRow }> = ({ row }) => {
  const { t } = useTranslation();
  if (row.total === null || row.previousTotal === null) return null;
  const diff = round1(row.total - row.previousTotal);
  if (diff === 0) {
    return (
      <Text size="xs" c="dimmed">
        {t('quarterlyScoring.results.same')}
      </Text>
    );
  }
  const grew = diff > 0;
  const Icon = grew ? IconArrowUpRight : IconArrowDownRight;
  return (
    <Group gap={2} wrap="nowrap" c={grew ? 'teal.7' : 'red.7'}>
      <Icon size={14} />
      <Text size="xs" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {grew ? '+' : ''}
        {diff}
      </Text>
    </Group>
  );
};

/** Правая часть строки: итог квартала, след правки и сравнение с прошлым. */
/** «Ответили 3 из 4»: отказ — тоже ответ; молчание — нет. */
const AnsweredCount: React.FC<{ row: ResultRow }> = ({ row }) => {
  const { t } = useTranslation();
  const answered = row.count + row.abstentions.length;
  return (
    <Text size="xs" c={row.pending.length > 0 ? 'orange' : 'dimmed'}>
      {t('quarterlyScoring.results.answered', {
        answered,
        total: answered + row.pending.length,
      })}
    </Text>
  );
};

const RowTotal: React.FC<{ row: ResultRow }> = ({ row }) => {
  const { t } = useTranslation();
  if (row.isHidden) {
    return (
      <Stack gap={2} align="flex-end">
        <Badge variant="light" color="gray">
          {t('quarterlyScoring.results.hiddenBadge')}
        </Badge>
        <AnsweredCount row={row} />
      </Stack>
    );
  }
  if (row.total === null) {
    // Не ноль: ноль — это оценка «всё плохо», отсутствие оценок — отсутствие данных.
    return (
      <Badge variant="light" color="gray">
        {t('quarterlyScoring.results.noScores')}
      </Badge>
    );
  }
  const isCorrected = row.finalScore !== null;
  return (
    <Stack gap={2} align="flex-end">
      <Group gap={8} wrap="nowrap">
        <Delta row={row} />
        <Text fw={700} size="lg" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.total}
        </Text>
      </Group>
      <Group gap={6} wrap="nowrap">
        {isCorrected && (
          <Group gap={3} wrap="nowrap" c="dimmed">
            <IconPencil size={12} />
            <Text size="xs">
              {t('quarterlyScoring.results.wasAverage', { value: row.average ?? '—' })}
            </Text>
          </Group>
        )}
        <AnsweredCount row={row} />
      </Group>
    </Stack>
  );
};

/** Блок правки итоговой оценки. Видит только тот, кто открыл эту страницу. */
const FinalScoreEditor: React.FC<{
  row: ResultRow;
  year: number;
  quarter: number;
}> = ({ row, year, quarter }) => {
  const { t } = useTranslation();
  const setFinal = useSetFinalScore();
  // Пустое поле, пока оценку не правили: подставленная средняя читалась бы как
  // уже выставленная правка, да ещё и округлённая до целого.
  const [value, setValue] = useState<number | ''>(row.finalScore ?? '');

  const save = (next: number | null) =>
    setFinal.mutate(
      { year, quarter, userId: row.userId, value: next },
      {
        onSuccess: () => {
          setValue(next ?? '');
          showToast({
            title:
              next === null
                ? t('quarterlyScoring.results.reset')
                : t('quarterlyScoring.results.saved'),
            message: '',
            color: 'teal',
          });
        },
      },
    );

  // Отчёта нет — итог хранить негде: правка живёт в отчёте за квартал.
  if (!row.reportId) return null;

  const isChanged = value !== '' && value !== row.finalScore;

  return (
    <Card withBorder radius="md" padding="sm" bg="var(--mantine-color-body)">
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
        <div>
          <Text size="sm" fw={500}>
            {t('quarterlyScoring.results.finalTitle')}
          </Text>
          <Text size="xs" c="dimmed" maw={420}>
            {t('quarterlyScoring.results.finalHint')}
          </Text>
        </div>
        <Group gap="xs" wrap="wrap" align="flex-end">
          <NumberInput
            w={110}
            min={MIN_SCORE}
            max={MAX_SCORE}
            allowDecimal={false}
            value={value}
            placeholder={row.average === null ? undefined : String(row.average)}
            onChange={(v) => setValue(typeof v === 'number' ? v : '')}
            // С именем: на экране таких полей столько же, сколько людей, и
            // «Итоговая оценка» без уточнения ничего не говорит.
            aria-label={`${t('quarterlyScoring.results.finalTitle')}: ${row.name}`}
          />
          <Button
            size="sm"
            disabled={!isChanged}
            loading={setFinal.isPending}
            onClick={() => typeof value === 'number' && save(value)}
          >
            {t('common.save')}
          </Button>
          {row.finalScore !== null && (
            <Button size="sm" variant="subtle" onClick={() => save(null)}>
              {t('quarterlyScoring.results.resetToAverage')}
            </Button>
          )}
        </Group>
      </Group>
      {row.finalScore !== null && row.finalScoreBy && (
        <Text size="xs" c="dimmed" mt={8}>
          {t('quarterlyScoring.results.correctedBy', {
            name: row.finalScoreBy,
            date: row.finalScoreAt ? dayjs(row.finalScoreAt).format('D MMMM YYYY') : '',
          })}
        </Text>
      )}
    </Card>
  );
};

const RowBody: React.FC<{
  row: ResultRow;
  year: number;
  quarter: number;
  /** Окно открыто: средняя ещё меняется, итог править не от чего. */
  isPreliminary: boolean;
  onOpenReport: (row: ResultRow) => void;
}> = ({ row, year, quarter, isPreliminary, onOpenReport }) => {
  const { t } = useTranslation();
  return (
    <Stack gap="md">
      {!isPreliminary && <FinalScoreEditor row={row} year={year} quarter={quarter} />}

      {row.isHidden && (
        <Alert variant="light" color="gray" icon={<IconInfoCircle size={16} />}>
          {t('quarterlyScoring.results.hiddenNotice')}
        </Alert>
      )}

      {row.pending.length > 0 && (
        <Text size="sm" c="orange">
          {t('quarterlyScoring.results.pending', {
            names: row.pending.map((p) => p.authorName).join(', '),
          })}
        </Text>
      )}

      {row.byRole.length > 0 && (
        <Group gap="xs" wrap="wrap">
          {row.byRole.map((r) => (
            <Badge key={r.role} variant="light">
              {t(`quarterlyScoring.roles.${r.role}`)}: {r.average}
              {r.count > 1 ? ` · ${t('quarterlyScoring.results.count', { count: r.count })}` : ''}
            </Badge>
          ))}
        </Group>
      )}

      {row.abstentions.map((a) => (
        <Card key={a.authorId} withBorder radius="md" padding="sm">
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={500}>
                {a.authorName}
              </Text>
              <Text size="xs" c="dimmed">
                {t(`quarterlyScoring.roles.${a.authorRole}`)}
              </Text>
            </div>
            <Badge variant="light" color="gray">
              {t('quarterlyScoring.abstained')}
            </Badge>
          </Group>
          <Text size="sm" mt={6} style={{ whiteSpace: 'pre-wrap' }}>
            {a.reason}
          </Text>
        </Card>
      ))}

      {row.isHidden ? null : row.scores.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t('quarterlyScoring.results.noScores')}
        </Text>
      ) : (
        <Stack gap="sm">
          {row.scores.map((score) => (
            <Card key={score.authorId} withBorder radius="md" padding="sm">
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500}>
                    {score.authorName}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {t(`quarterlyScoring.roles.${score.authorRole}`)}
                  </Text>
                </div>
                <Text fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {score.value}
                </Text>
              </Group>
              {score.comment && (
                <Text size="sm" mt={6} style={{ whiteSpace: 'pre-wrap' }}>
                  {score.comment}
                </Text>
              )}
            </Card>
          ))}
        </Stack>
      )}

      {row.reportId && (
        <Group>
          <Button
            variant="light"
            leftSection={<IconFileText size={16} />}
            onClick={() => onOpenReport(row)}
          >
            {t('quarterlyScoring.results.openReport')}
          </Button>
        </Group>
      )}
    </Stack>
  );
};

/** Текст отчёта — в боковой панели: внутри аккордеона его пришлось бы читать во вложенном скролле. */
const ReportDrawer: React.FC<{ row: ResultRow | null; onClose: () => void }> = ({
  row,
  onClose,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuarterlyReport(row?.reportId || undefined);
  const items = data ? pruneNodes(data.report.items) : [];

  return (
    <Drawer
      opened={!!row}
      onClose={onClose}
      position="right"
      size="xl"
      padding="lg"
      title={
        <div>
          <Text fw={600}>{row?.name}</Text>
          {row?.position && (
            <Text size="xs" c="dimmed">
              {row.position}
            </Text>
          )}
        </div>
      }
    >
      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : error ? (
        // Несданный отчёт держателю права не открывается — человек его ещё пишет.
        <Text size="sm" c="dimmed">
          {t('quarterlyScoring.results.reportNotSubmitted')}
        </Text>
      ) : items.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t('quarterly.empty')}
        </Text>
      ) : (
        <NodeListPreview nodes={items} />
      )}
    </Drawer>
  );
};

interface TeamGroup {
  team: string;
  rows: ResultRow[];
  average: number | null;
}

/** Люди под своей первой командой; кто ни в одной — в конце, отдельной группой. */
function groupByTeam(rows: ResultRow[], noTeamLabel: string): TeamGroup[] {
  const groups = new Map<string, ResultRow[]>();
  for (const row of rows) {
    const team = row.teams[0] || noTeamLabel;
    groups.set(team, [...(groups.get(team) || []), row]);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => (a === noTeamLabel ? 1 : b === noTeamLabel ? -1 : a.localeCompare(b, 'ru')))
    .map(([team, teamRows]) => {
      const scored = teamRows.filter((r) => r.total !== null);
      return {
        team,
        rows: teamRows,
        average: scored.length
          ? round1(scored.reduce((acc, r) => acc + (r.total as number), 0) / scored.length)
          : null,
      };
    });
}

/**
 * Итоги квартала. Открыты только держателю права «видит все оценки» и только
 * после закрытия окна: половина оценок — ещё не результат, а по нему принимают
 * решение о премии.
 */
export const QuarterlyScoringResults: React.FC = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(() => reportingQuarter());
  const [openedReport, setOpenedReport] = useState<ResultRow | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const { data, isLoading, error } = useScoreResults(period.year, period.quarter, true, true);
  const rows = data?.rows;
  const isPreliminary = data?.state === 'open';

  const quarterOptions = useMemo(
    () =>
      [1, 2, 3, 4].map((q) => ({ value: String(q), label: t('quarterly.quarterOption', { q }) })),
    [t],
  );
  const yearOptions = useMemo(() => {
    const y = dayjs().year();
    return Array.from({ length: 5 }, (_, i) => String(y - 3 + i));
  }, []);

  const groups = useMemo(
    () => groupByTeam(rows || [], t('quarterlyScoring.results.noTeam')),
    [rows, t],
  );

  const status = error instanceof ApiError ? error.status : null;

  const exportXlsx = async () => {
    setIsExporting(true);
    try {
      await downloadResultsXlsx(period.year, period.quarter);
    } catch {
      showToast({
        title: t('common.error'),
        message: t('quarterlyScoring.results.exportFailed'),
        color: 'red',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageContainer>
      <Group justify="space-between" mb="xs" wrap="wrap" align="flex-start">
        <div>
          <Title order={2}>
            {t('quarterlyScoring.results.titleFor', {
              quarter: period.quarter,
              year: period.year,
            })}
          </Title>
          <Text size="sm" c="dimmed" mt={4}>
            {t('quarterlyScoring.results.hint')}
          </Text>
        </div>
        <Group gap="sm">
          <Group gap={4} wrap="nowrap">
            <ActionIcon
              variant="default"
              size="lg"
              aria-label={t('quarterlyScoring.results.previousQuarter')}
              onClick={() => setPeriod((p) => shiftQuarter(p, -1))}
            >
              <IconChevronLeft size={18} />
            </ActionIcon>
            <Select
              w={150}
              data={quarterOptions}
              value={String(period.quarter)}
              onChange={(v) => v && setPeriod((p) => ({ ...p, quarter: Number(v) }))}
              allowDeselect={false}
            />
            <Select
              w={110}
              data={yearOptions}
              value={String(period.year)}
              onChange={(v) => v && setPeriod((p) => ({ ...p, year: Number(v) }))}
              allowDeselect={false}
            />
            <ActionIcon
              variant="default"
              size="lg"
              // Дальше отчётного квартала итогов не существует: окно ещё не
              // закрылось, и сервер всё равно ответит отказом.
              disabled={!isBeforeQuarter(period, reportingQuarter())}
              aria-label={t('quarterlyScoring.results.nextQuarter')}
              onClick={() => setPeriod((p) => shiftQuarter(p, 1))}
            >
              <IconChevronRight size={18} />
            </ActionIcon>
          </Group>
          <Button
            variant="light"
            leftSection={<IconTableExport size={16} />}
            loading={isExporting}
            disabled={!rows || rows.length === 0}
            onClick={() => void exportXlsx()}
          >
            {t('quarterlyScoring.results.export')}
          </Button>
        </Group>
      </Group>

      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : status === 409 ? (
        <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />}>
          {/* 409 бывает только до открытия окна: после открытия итоги предварительные. */}
          {t('quarterlyScoring.results.windowNotOpened')}
        </Alert>
      ) : status === 403 ? (
        <Alert variant="light" color="gray" icon={<IconInfoCircle size={16} />}>
          {t('quarterlyScoring.results.noAccess')}
        </Alert>
      ) : error ? (
        <LoadFailed message={t('errors.loadFailed')} hasRetry />
      ) : groups.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          {t('quarterlyScoring.results.empty')}
        </Text>
      ) : (
        <Stack gap="xl" mt="lg">
          {isPreliminary && (
            <Alert variant="light" color="yellow" icon={<IconInfoCircle size={16} />}>
              {t('quarterlyScoring.results.preliminary', {
                date: data?.lastDay ? dayjs(data.lastDay).format('D MMMM') : '',
              })}
            </Alert>
          )}
          {groups.map((group) => (
            <div key={group.team}>
              <Group justify="space-between" align="baseline" wrap="nowrap" mb={6}>
                <Text fw={600}>{group.team}</Text>
                <Text size="xs" c="dimmed">
                  {t('quarterlyScoring.results.people', { count: group.rows.length })}
                  {group.average !== null
                    ? ` · ${t('quarterlyScoring.results.teamAverage', { value: group.average })}`
                    : ''}
                </Text>
              </Group>
              <Divider mb="xs" />
              <Accordion variant="separated">
                {group.rows.map((row) => (
                  <Accordion.Item key={row.userId} value={row.userId}>
                    <Accordion.Control>
                      <Group justify="space-between" wrap="nowrap" pr="md">
                        <div style={{ minWidth: 0 }}>
                          <Group gap="xs" wrap="nowrap">
                            <Text fw={600}>{row.name}</Text>
                            {/* Уволенного оставляем в итогах: работу он сделал и
                                оценки получил. Но пометить надо, иначе непонятно,
                                почему он в списке. */}
                            {row.isActive === false && (
                              <Badge size="xs" variant="light" color="gray">
                                {t('quarterlyScoring.results.inactive')}
                              </Badge>
                            )}
                          </Group>
                          {row.position && (
                            <Text size="xs" c="dimmed">
                              {row.position}
                            </Text>
                          )}
                        </div>
                        <RowTotal row={row} />
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <RowBody
                        row={row}
                        year={period.year}
                        quarter={period.quarter}
                        isPreliminary={isPreliminary}
                        onOpenReport={setOpenedReport}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </div>
          ))}
        </Stack>
      )}

      <ReportDrawer row={openedReport} onClose={() => setOpenedReport(null)} />
    </PageContainer>
  );
};
